/**
 * PaymentMethodSelector — Shared portal payment method selector and execution component.
 *
 * Canonical payment lifecycle is entirely backend-controlled:
 *   • Stripe: checkout session → provider confirmation → apply_payment()
 *   • PayPal: create order → PayPal JS SDK button → capture → apply_payment()
 *   • Manual (Wise/Thai Bank/Thai QR/Wire): instructions → report submitted → staff Verify & Post → apply_payment()
 */
import { useEffect, useState, useCallback } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";

// ── Currency formatter ─────────────────────────────────────────────────────
const fmt = (v, ccy = "USD") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
  catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
};

// ── Canonical client-facing display labels (P10) ──────────────────────────
const METHOD_DISPLAY = {
  stripe:    "Credit / Debit Card",
  paypal:    "PayPal",
  wise:      "Wise",
  thai_bank: "Thai Direct Bank Transfer",
  thai_qr:   "Thai QR Code",
  wire:      "International Wire Transfer",
};

// ── Method icon map ────────────────────────────────────────────────────────
const METHOD_ICONS = {
  stripe:    "CreditCard",
  paypal:    "Wallet",
  wise:      "ArrowRightLeft",
  thai_bank: "Building2",
  thai_qr:   "QrCode",
  wire:      "Globe",
};

// ── Method accent colors ────────────────────────────────────────────────────
const METHOD_COLORS = {
  stripe:    "bg-violet-50 border-violet-200 text-violet-700",
  paypal:    "bg-blue-50 border-blue-200 text-blue-700",
  wise:      "bg-emerald-50 border-emerald-200 text-emerald-700",
  thai_bank: "bg-amber-50 border-amber-200 text-amber-700",
  thai_qr:   "bg-teal-50 border-teal-200 text-teal-700",
  wire:      "bg-slate-50 border-slate-200 text-slate-700",
};

// ── Copy field ────────────────────────────────────────────────────────────
function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-all">{value}</div>
      </div>
      <button onClick={copy}
        className="ml-3 shrink-0 text-slate-400 hover:text-[#F5A623] transition-colors"
        title="Copy">
        {copied ? <Icons.Check className="h-3.5 w-3.5 text-emerald-500" /> : <Icons.Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

// ── Fee breakdown ─────────────────────────────────────────────────────────
function FeeBreakdown({ method }) {
  if (!method.fee_amount || method.fee_amount <= 0) return null;
  const ccy = method.currency || "USD";
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="fee-breakdown">
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between text-slate-600">
          <span>Invoice balance</span><span>{fmt(method.invoice_balance, ccy)}</span>
        </div>
        <div className="flex justify-between text-amber-700">
          <span>Processing fee ({method.fee_pct}%)</span><span>+{fmt(method.fee_amount, ccy)}</span>
        </div>
        <div className="flex justify-between font-bold text-slate-900 border-t border-amber-200 pt-1.5 mt-1.5">
          <span>Total to send via {METHOD_DISPLAY[method.method_code] || method.display_name}</span>
          <span data-testid="fee-total-payable">{fmt(method.total_payable, ccy)}</span>
        </div>
      </div>
      <p className="text-xs text-amber-700 mt-2">
        Your advisor will post only {fmt(method.invoice_balance, ccy)} against your invoice balance. The fee is retained by the payment provider.
      </p>
    </div>
  );
}

// ── Proof uploader (required for Thai Bank / Thai QR) ─────────────────────
// Uses the canonical portal document upload path.
// Accepted types: PNG, JPG/JPEG, PDF — the most common bank screenshot/receipt formats.
function ProofUploader({ onUploaded, disabled }) {
  const [state, setState] = useState("idle"); // idle | uploading | done | error
  const [fileName, setFileName] = useState("");
  const fileRef = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Only PNG, JPG, or PDF files are accepted as payment proof.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20 MB.");
      return;
    }
    setState("uploading");
    setFileName(file.name);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_type", "payment_proof");
      const { data } = await portalApi.post("/portal/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState("done");
      onUploaded(data.id || data.document_id);
    } catch (e) {
      setState("error");
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Upload failed. Please try again.");
    }
  };

  return (
    <div data-testid="proof-uploader">
      {state === "done" ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-700">Proof uploaded</p>
            <p className="text-xs text-emerald-600 truncate">{fileName}</p>
          </div>
          <button
            onClick={() => { setState("idle"); setFileName(""); onUploaded(null); }}
            className="ml-auto text-xs text-slate-400 hover:text-slate-600"
            data-testid="proof-uploader-remove">
            Remove
          </button>
        </div>
      ) : (
        <label
          className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-colors p-5
            ${disabled ? "opacity-50 pointer-events-none border-slate-200 bg-slate-50"
              : state === "uploading" ? "border-amber-300 bg-amber-50"
              : state === "error"    ? "border-rose-300 bg-rose-50"
              : "border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100"}`}
          data-testid="proof-uploader-dropzone">
          {state === "uploading" ? (
            <Icons.Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          ) : (
            <Icons.Upload className="h-6 w-6 text-amber-600" />
          )}
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-700">
              {state === "uploading" ? "Uploading…" : state === "error" ? "Upload failed — try again" : "Upload Payment Confirmation"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Screenshot, bank receipt, or deposit slip (PNG, JPG, PDF · max 20 MB)</p>
          </div>
          <input ref={fileRef[0]} type="file" accept=".png,.jpg,.jpeg,.pdf" className="sr-only"
            onChange={e => handleFile(e.target.files?.[0])}
            data-testid="proof-uploader-input" />
        </label>
      )}
    </div>
  );
}

// ── Manual report form (Wise / Thai Bank / Thai QR / Wire) ────────────────
function ManualReportForm({ method, invoice, onSubmitted, hasExternalPayStarted }) {
  const isThaiMethod = method.method_code === "thai_bank" || method.method_code === "thai_qr";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [savePreference, setSavePreference] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Thai-method specific fields
  const [thbAmount, setThbAmount] = useState(() => {
    if (!isThaiMethod) return "";
    if (method.thb_amount) return String(Math.round(method.thb_amount));
    // THB invoice → THB local: invoice balance is the exact payment amount, no conversion
    if (invoice.currency === "THB") return String(Math.round(invoice.balance));
    return "";
  });
  const [proofDocumentId, setProofDocumentId] = useState(null);

  const canSubmit = !submitting && (!isThaiMethod || proofDocumentId);

  const submit = async () => {
    if (!canSubmit) return;
    if (isThaiMethod && !proofDocumentId) {
      toast.error("Please upload your payment confirmation before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        method_code: method.method_code,
        payment_date: date || null,
        reference_number: ref.trim() || null,
        notes: notes.trim() || null,
        save_preference: savePreference,
      };
      if (isThaiMethod && proofDocumentId) {
        payload.proof_document_id = proofDocumentId;
        if (thbAmount) payload.source_amount = parseFloat(thbAmount) || undefined;
      }
      const { data } = await portalApi.post(`/portal/billing/${invoice.id}/payment-report`, payload);
      if (data.duplicate) {
        toast.info("A payment report already exists for this invoice.");
      } else {
        toast.success("Payment reported. Our Finance team will verify it shortly.");
      }
      onSubmitted(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === "object" ? detail?.message : detail) || "Could not submit report. Please try again.";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3 pt-4" data-testid="manual-report-form">
      {/* Prominent next-step banner — shown after client has initiated the external payment */}
      {hasExternalPayStarted ? (
        <div className="rounded-xl border-2 border-[#F5A623] bg-amber-50 p-4" data-testid="report-required-banner">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-[#F5A623] flex items-center justify-center shrink-0 mt-0.5">
              <Icons.AlertCircle className="h-4 w-4 text-[#171B2E]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#171B2E]">Required: Report your payment</p>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                Your payment won't be applied until you confirm it here. This lets our Finance team verify the transfer.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Already sent your payment? Confirm it here
          </p>
        </div>
      )}

      {/* Thai: THB amount paid */}
      {isThaiMethod && (
        <div>
          <label className="block text-xs text-slate-500 mb-1">Amount Paid in Thai Baht (฿)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">฿</span>
            <input type="number" min="0" step="1" value={thbAmount} onChange={e => setThbAmount(e.target.value)}
              placeholder={method.thb_amount ? String(Math.round(method.thb_amount)) : invoice.currency === "THB" ? String(Math.round(invoice.balance)) : "e.g. 9075"}
              className="w-full border border-slate-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
              data-testid="report-form-thb-amount" />
          </div>
          <p className="text-xs text-slate-400 mt-1">Enter the exact THB amount shown in your banking app or receipt.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Payment Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
            data-testid="report-form-date" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Transaction / Reference ID</label>
          <input type="text" value={ref} onChange={e => setRef(e.target.value)}
            placeholder="e.g. TXN-123456"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
            data-testid="report-form-reference" />
        </div>
      </div>

      <div>
        <label className="block text-xs text-slate-500 mb-1">Notes (optional)</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
          placeholder="Any additional details…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
          data-testid="report-form-notes" />
      </div>

      {/* Thai: required proof upload */}
      {isThaiMethod && (
        <div>
          <div className="flex items-center gap-1 mb-1.5">
            <label className="text-xs font-semibold text-slate-600">Payment Confirmation</label>
            <span className="text-xs font-bold text-rose-500">*Required</span>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Upload a screenshot of your completed mobile banking payment, QR confirmation, or bank receipt.
            Our Finance team must see this before verifying your payment.
          </p>
          <ProofUploader onUploaded={setProofDocumentId} disabled={submitting} />
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={savePreference} onChange={e => setSavePreference(e.target.checked)}
          className="h-4 w-4 rounded accent-[#F5A623]" data-testid="report-form-save-pref" />
        <span className="text-xs text-slate-600">Remember this payment method for next time</span>
      </label>

      <button onClick={submit} disabled={!canSubmit}
        className={`w-full inline-flex items-center justify-center gap-2 text-[#171B2E] font-semibold rounded-lg py-2.5 text-sm transition-colors
          ${!canSubmit
            ? "opacity-50 cursor-not-allowed bg-[#F5A623]"
            : hasExternalPayStarted
              ? "bg-[#F5A623] hover:bg-[#e0951a] shadow-md shadow-amber-200"
              : "bg-[#F5A623] hover:bg-[#e0951a]"
          }`}
        data-testid="report-form-submit">
        {submitting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle className="h-4 w-4" />}
        {submitting ? "Submitting…" : "Submit Payment for Verification"}
      </button>

      {isThaiMethod && !proofDocumentId && (
        <p className="text-xs text-rose-500 text-center" data-testid="proof-required-hint">
          Upload your payment confirmation to enable submission.
        </p>
      )}

      <p className="text-xs text-slate-400 text-center">
        Our Finance team will verify your transfer and post the payment. Your invoice won't show as paid until verification is complete.
      </p>
    </div>
  );
}

// ── QR image component ─────────────────────────────────────────────────────
function QRImage({ methodCode }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    portalApi.get("/portal/payment-config/qr-image",
      { params: { method_code: methodCode }, responseType: "blob" })
      .then(r => setSrc(URL.createObjectURL(r.data)))
      .catch(() => setSrc(null));
  }, [methodCode]);
  if (!src) return (
    <div className="h-48 w-48 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
      <Icons.Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
  return <img src={src} alt="QR Payment Code" className="h-52 w-52 rounded-xl border border-slate-200 shadow-sm mx-auto" />;
}

// ── Stripe checkout ────────────────────────────────────────────────────────
function StripeContent({ invoice, method, returnPath, onInitiated }) {
  const [paying, setPaying] = useState(false);

  // Cross-currency: for a THB canonical invoice, Stripe settles in USD.
  // The usd_amount (indicative) is shown before the Pay button.
  // The actual locked USD amount is calculated server-side at checkout.
  const isCrossCurrency = invoice.currency === "THB" && method?.usd_amount;
  const displayCurrency = isCrossCurrency ? "USD" : (invoice.currency || "USD");
  const displayAmount   = isCrossCurrency ? method.usd_amount : invoice.balance;
  const rateAsOf        = method?.rate_as_of;
  const fxSource        = method?.fx_source;
  const rateLabel       = fxSource === "manual_fallback"
    ? "Manual rate set by your advisor"
    : rateAsOf
      ? `Current exchange rate · as of ${rateAsOf}`
      : null;

  const payNow = async () => {
    setPaying(true);
    try {
      if (returnPath) sessionStorage.setItem("tf_portal_payment_return", returnPath);
      sessionStorage.setItem("tf_portal_pending_payment",
        JSON.stringify({ invoiceNumber: invoice.invoice_number, amount: invoice.balance, currency: invoice.currency }));
      const { data } = await portalApi.post(`/portal/billing/${invoice.id}/checkout`,
        { origin_url: window.location.origin });
      if (data.url) {
        onInitiated?.();
        window.location.href = data.url;
      }
    } catch (e) {
      if (returnPath) sessionStorage.removeItem("tf_portal_payment_return");
      sessionStorage.removeItem("tf_portal_pending_payment");
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 503) {
        toast.error(detail || "Exchange rate temporarily unavailable. Please try again shortly or choose another payment method.");
      } else {
        toast.error(detail || "Could not start payment. Please try again.");
      }
      setPaying(false);
    }
  };

  return (
    <div className="pt-3" data-testid="stripe-payment-content">
      {isCrossCurrency && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3 text-sm" data-testid="stripe-cross-currency-summary">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-slate-500">Invoice Amount</span>
            <span className="font-semibold text-slate-800" data-testid="stripe-canonical-amount">
              ฿{new Intl.NumberFormat("th-TH").format(invoice.balance)} THB
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 pt-1.5">
            <span className="text-slate-500">Card Payment Amount</span>
            <span className="font-bold text-[#171B2E]" data-testid="stripe-usd-settlement-amount">
              {fmt(displayAmount, "USD")}
            </span>
          </div>
          {rateLabel && (
            <p className="text-[11px] text-slate-400 mt-2" data-testid="stripe-fx-rate-info">{rateLabel}</p>
          )}
          <p className="text-[11px] text-slate-400 mt-1">
            Your invoice is denominated in Thai Baht. Credit and debit card payments are processed
            in USD using the current exchange rate. The exact amount is locked when you confirm payment.
          </p>
        </div>
      )}
      <button onClick={payNow} disabled={paying}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-xl py-3.5 text-sm shadow-sm"
        data-testid="stripe-pay-now">
        {paying ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CreditCard className="h-4 w-4" />}
        {paying ? "Redirecting to secure payment…" : `Pay ${fmt(displayAmount, displayCurrency)}`}
      </button>
      <p className="text-center text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
        <Icons.Lock className="h-3 w-3" />Secure payment powered by Stripe
      </p>
    </div>
  );
}

// ── PayPal checkout ────────────────────────────────────────────────────────
function PayPalContent({ invoice, ppClientId, onPaid }) {
  const [ordering, setOrdering] = useState(false);
  const [error, setError]       = useState(null);

  const createOrder = async () => {
    setOrdering(true);
    setError(null);
    try {
      const { data } = await portalApi.post(`/portal/billing/${invoice.id}/paypal-order`,
        { origin_url: window.location.origin });
      return data.order_id;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not initiate PayPal. Please try again.");
      setOrdering(false);
      throw e;
    }
  };

  const onApprove = async (data) => {
    setOrdering(true);
    try {
      const res = await portalApi.post(`/portal/billing/${invoice.id}/paypal-capture`,
        { order_id: data.orderID });
      if (res.data.status === "paid" || res.data.status === "already_processed") {
        toast.success("Payment received! Your invoice will be updated shortly.");
        onPaid?.();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not verify PayPal payment. Please contact support.");
    } finally { setOrdering(false); }
  };

  const onCancel = () => {
    toast.info("PayPal payment cancelled.");
  };

  const onError = (err) => {
    console.error("PayPal error", err);
    setError("PayPal encountered an error. Please try again or choose another payment method.");
  };

  return (
    <div className="pt-3 space-y-3" data-testid="paypal-payment-content">
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-sm text-slate-600">
        <div className="flex justify-between mb-1">
          <span>Invoice balance</span>
          <span className="font-semibold text-slate-900">{fmt(invoice.balance, invoice.currency)}</span>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          You will be redirected to PayPal to complete your payment securely.
        </p>
      </div>
      {ordering ? (
        <div className="flex justify-center py-4">
          <Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" />
        </div>
      ) : (
        <PayPalScriptProvider options={{
          "client-id": ppClientId,
          currency: invoice.currency || "USD",
          intent: "capture",
          components: "buttons",
        }}>
          <PayPalButtons
            style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
            createOrder={createOrder}
            onApprove={onApprove}
            onCancel={onCancel}
            onError={onError}
          />
        </PayPalScriptProvider>
      )}
      <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
        <Icons.Lock className="h-3 w-3" />Secured by PayPal
      </p>
    </div>
  );
}

// ── Instruction content (Wise / Thai Bank / Thai QR / Wire) ───────────────
function InstructionContent({ method, invoice, onReportSubmitted }) {
  const isThaiMethod = method.method_code === "thai_bank" || method.method_code === "thai_qr";
  const ccy = invoice.currency || "USD";
  const ref = method.payment_reference_format?.replace("{invoice_number}", invoice.invoice_number)
              || invoice.invoice_number;
  // Track when client has clicked/initiated the external payment action this session.
  // Persisted via sessionStorage so it survives a tab nav and return without losing state.
  const storageKey = `tf_pay_started_${invoice.id}_${method.method_code}`;
  const [hasExternalPayStarted, setHasExternalPayStarted] = useState(
    () => sessionStorage.getItem(storageKey) === "1"
  );

  const markPayStarted = async () => {
    sessionStorage.setItem(storageKey, "1");
    setHasExternalPayStarted(true);
    // For Thai methods: call backend to create proof obligation (drives action-center)
    if (isThaiMethod) {
      try {
        await portalApi.post(`/portal/billing/${invoice.id}/payment-started`, {
          method_code: method.method_code,
        });
      } catch {
        // Non-blocking — action-center item is best-effort; the UI flow continues
      }
    }
  };

  // THB settlement amount (backend-computed, presentational only)
  const thbDisplay = isThaiMethod && method.thb_amount
    ? new Intl.NumberFormat("th-TH").format(Math.round(method.thb_amount))
    : null;

  return (
    <div className="pt-3 space-y-4" data-testid={`${method.method_code}-payment-content`}>
      {method.instructions && (
        <p className="text-sm text-slate-600 leading-relaxed">{method.instructions}</p>
      )}

      {/* Thai: settlement amount — behavior depends on invoice currency */}
      {isThaiMethod && (
        ccy === "THB" ? (
          /* THB invoice → THB local payment: same currency, no FX conversion */
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="thb-settlement-amount">
            <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">Amount to Pay</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-amber-800">
                ฿{new Intl.NumberFormat("th-TH").format(Math.round(method.invoice_balance ?? invoice.balance))}
              </span>
              <span className="text-sm text-amber-600">THB</span>
            </div>
          </div>
        ) : thbDisplay ? (
          /* USD invoice → THB local payment: FX conversion required */
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="thb-settlement-amount">
            <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">Amount to Pay in Thai Baht</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-amber-800">฿{thbDisplay}</span>
              <span className="text-sm text-amber-600">THB</span>
            </div>
            <p className="text-xs text-amber-700 mt-1.5">
              Approximate amount at current exchange rate ({method.usd_thb_rate?.toFixed(2) || "33.00"} THB per 1 USD).
              Exact amount may differ slightly depending on your bank's rate at time of transfer.
            </p>
            <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-amber-600">
              Invoice amount: <span className="font-semibold">{fmt(method.invoice_balance, "USD")}</span>
            </div>
          </div>
        ) : null
      )}

      <FeeBreakdown method={{ ...method, currency: ccy }} />

      {/* PayPal (manual fallback — should not appear when API-based PayPal is active) */}
      {method.method_code === "paypal" && method.paypal_email && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <CopyField label="PayPal Account" value={method.paypal_email} />
          {!method.fee_amount && <CopyField label="Amount to Send" value={fmt(method.invoice_balance, ccy)} />}
          {method.fee_amount > 0 && <CopyField label="Amount to Send (includes fee)" value={fmt(method.total_payable, ccy)} />}
          <CopyField label="Payment Reference" value={ref} />
        </div>
      )}

      {/* Wise */}
      {method.method_code === "wise" && (() => {
        const wd = method.wise_details || {};
        const tagOk  = wd.wise_tag_enabled  && wd.wise_tag;
        const linkOk = wd.payment_link_enabled && wd.payment_link;
        const qrOk   = wd.qr_enabled && wd.qr_document_id;
        return (
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">Amount to Pay</div>
              <div className="text-lg font-bold text-slate-900 mb-1">
                {fmt(method.invoice_balance, ccy)}
              </div>
              <div className="text-xs text-slate-400">Invoice: {method.payment_reference_format?.replace("{invoice_number}", invoice.invoice_number) || invoice.invoice_number}</div>
            </div>

            {tagOk && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="wise-tag-section">
                <div className="flex items-center gap-2 mb-2">
                  <Icons.AtSign className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise Tag</span>
                </div>
                <CopyField label="Send to" value={wd.wise_tag} />
                <p className="text-xs text-slate-500 mt-1">Find this Wise Tag in the Wise app and send your payment directly.</p>
                <button
                  onClick={markPayStarted}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-2 text-sm transition-colors"
                  data-testid="wise-tag-pay-btn">
                  <Icons.CheckCircle className="h-4 w-4" />
                  I've Sent to this Wise Tag
                </button>
              </div>
            )}

            {linkOk && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="wise-link-section">
                <div className="flex items-center gap-2 mb-2">
                  <Icons.Link className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise Payment Page</span>
                </div>
                <a href={wd.payment_link} target="_blank" rel="noreferrer"
                  onClick={markPayStarted}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors"
                  data-testid="wise-pay-button">
                  <Icons.ExternalLink className="h-4 w-4" />
                  Pay with Wise
                </a>
                <p className="text-xs text-slate-400 text-center mt-2">Opens Wise in a new tab. Come back here to report your payment once complete.</p>
              </div>
            )}

            {qrOk && (
              <div className="border border-slate-200 rounded-xl p-4" data-testid="wise-qr-section">
                <div className="flex items-center gap-2 mb-3">
                  <Icons.QrCode className="h-4 w-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise QR Code</span>
                </div>
                <QRImage methodCode="wise" />
                <p className="text-xs text-slate-500 text-center mt-2">Scan this QR code with Wise to make your payment.</p>
                <button
                  onClick={markPayStarted}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg py-2 text-sm transition-colors"
                  data-testid="wise-qr-pay-btn">
                  <Icons.QrCode className="h-4 w-4" />
                  I've Scanned the QR Code
                </button>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <CopyField label="Payment Reference" value={ref} />
              <p className="text-xs text-slate-500 mt-1">Please include your invoice number as the payment reference if Wise gives you the option to add one.</p>
            </div>
          </div>
        );
      })()}

      {/* Thai Bank — canonical field order */}
      {method.method_code === "thai_bank" && method.bank_details && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          {/* Render Thai bank details in canonical order.
              phone_number is checked by multiple known field names to handle
              existing configs that stored the value under a different key. */}
          {(() => {
            const bd = method.bank_details;
            const phoneVal = bd.phone_number || bd.mobile_number || bd.mobile || bd.phone || "";
            return (
              <>
                {bd.bank_name      && <CopyField label="Bank Name"       value={String(bd.bank_name)} />}
                {bd.account_name   && <CopyField label="Account Name"    value={String(bd.account_name)} />}
                {bd.account_number && <CopyField label="Account Number"  value={String(bd.account_number)} />}
                {phoneVal          && <CopyField label="Phone Number"    value={String(phoneVal)} />}
                {bd.branch         && <CopyField label="Branch"          value={String(bd.branch)} />}
                {(bd.swift_code || bd.swift_bic) && <CopyField label="SWIFT / BIC" value={String(bd.swift_code || bd.swift_bic)} />}
                {bd.iban           && <CopyField label="IBAN"            value={String(bd.iban)} />}
              </>
            );
          })()}
          <CopyField label="Payment Reference" value={ref} />
          <button
            onClick={markPayStarted}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg py-2 text-sm transition-colors"
            data-testid="bank-transfer-sent-btn">
            <Icons.CheckCircle className="h-4 w-4" />
            I've Made This Transfer
          </button>
        </div>
      )}

      {/* Wire — generic bank_details rendering (non-Thai, field order varies by wire type) */}
      {method.method_code === "wire" && method.bank_details && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          {Object.entries(method.bank_details).map(([k, v]) =>
            v ? <CopyField key={k} label={k.replace(/_/g, " ")} value={String(v)} /> : null
          )}
          <CopyField label="Currency" value={ccy} />
          <CopyField label="Amount" value={fmt(method.invoice_balance, ccy)} />
          <CopyField label="Payment Reference" value={ref} />
        </div>
      )}

      {/* Thai QR */}
      {method.method_code === "thai_qr" && (
        <div className="flex flex-col items-center gap-3 py-2">
          {method.qr_document_id ? (
            <QRImage methodCode={method.method_code} />
          ) : (
            <div className="h-48 w-48 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
              <Icons.QrCode className="h-16 w-16 text-slate-300" />
            </div>
          )}
          <CopyField label="Payment Reference" value={ref} />
          <button
            onClick={markPayStarted}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg py-2 text-sm transition-colors"
            data-testid="thai-qr-scanned-btn">
            <Icons.QrCode className="h-4 w-4" />
            I've Scanned and Paid
          </button>
        </div>
      )}

      <ManualReportForm method={method} invoice={invoice} onSubmitted={onReportSubmitted} hasExternalPayStarted={hasExternalPayStarted} />
    </div>
  );
}

// ── Pending verification state ─────────────────────────────────────────────
function PendingVerificationState({ report, invoice, onCancel }) {
  const ccy = report.currency || invoice.currency;
  return (
    <div className="text-center py-6" data-testid="pending-verification-state">
      <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
        <Icons.Clock className="h-7 w-7 text-amber-600" />
      </div>
      <h3 className="font-semibold text-slate-800 text-lg">Payment Submitted — Verification Pending</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
        Your payment report has been received. Our Finance team will verify the transfer and post it shortly. Your invoice will show as Paid once verified.
      </p>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4 text-left max-w-sm mx-auto">
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Method</span>
            <span className="font-medium capitalize">
              {METHOD_DISPLAY[report.method_code] || (report.method_code || "").replace(/_/g, " ")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Amount Reported</span>
            <span className="font-medium">{fmt(report.amount_reported, ccy)}</span>
          </div>
          {report.reference_number && (
            <div className="flex justify-between">
              <span className="text-slate-500">Reference</span>
              <span className="font-medium font-mono text-xs">{report.reference_number}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">Reported on</span>
            <span className="font-medium">{report.created_at?.slice(0, 10)}</span>
          </div>
        </div>
      </div>
      {onCancel && (
        <button onClick={onCancel} className="mt-4 text-xs text-slate-400 hover:text-slate-600 hover:underline"
          data-testid="pending-verification-exit">
          Exit to dashboard
        </button>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function PaymentMethodSelector({ invoiceId, returnPath, onPaymentInitiated, onReportSubmitted, onExitDashboard }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [ppConfig, setPpConfig] = useState(null);
  // Thai sub-method: which of thai_bank / thai_qr is active within the "Pay Locally" group
  const [thaiSubMethod, setThaiSubMethod] = useState("thai_bank");

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: pp }] = await Promise.all([
        portalApi.get(`/portal/billing/${invoiceId}/payment-methods`),
        portalApi.get("/portal/paypal/config").catch(() => ({ data: { configured: false } })),
      ]);
      setData(d);
      setPpConfig(pp);
      const available = d.methods || [];
      const pref = d.saved_preference;
      const preferred = available.find(m => m.method_code === pref);
      if (!selected) {
        setSelected(preferred?.method_code || available[0]?.method_code || null);
      }
    } catch {
      toast.error("Could not load payment options. Please refresh.");
    } finally { setLoading(false); }
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex justify-center py-12" data-testid="payment-methods-loading">
      <Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" />
    </div>
  );

  if (!data) return (
    <p className="text-sm text-slate-500 text-center py-8">Could not load payment options. Please refresh.</p>
  );

  const { methods = [], invoice, pending_report } = data;

  // ── Group Thai methods under one top-level option ───────────────────────
  // thai_bank and thai_qr are settlement methods for the same underlying payment
  // action (pay in Thai Baht locally). They share one top-level "Pay Locally in
  // Thai Baht" choice, with a secondary sub-selector to pick the delivery method.
  const thaiMethods = methods.filter(m => m.method_code === "thai_bank" || m.method_code === "thai_qr");
  const nonThaiMethods = methods.filter(m => m.method_code !== "thai_bank" && m.method_code !== "thai_qr");
  const hasThaiGroup = thaiMethods.length > 0;
  // Virtual method code used to represent the Thai group in the top-level selector
  const THAI_GROUP_CODE = "thai_local";
  // Build the list of top-level display methods (non-Thai + Thai group at end if applicable)
  const displayMethods = hasThaiGroup
    ? [...nonThaiMethods, {
        method_code: THAI_GROUP_CODE,
        display_name: "Pay Locally in Thai Baht",
        subtitle: "Bank transfer or QR code — pay in Thai Baht",
        thb_amount: thaiMethods[0]?.thb_amount,
        usd_thb_rate: thaiMethods[0]?.usd_thb_rate,
        invoice_balance: thaiMethods[0]?.invoice_balance,
        fee_pct: 0,
      }]
    : nonThaiMethods;
  // The actual method config to pass to InstructionContent when Thai is selected
  const activeThaiMethod = thaiMethods.find(m => m.method_code === thaiSubMethod) || thaiMethods[0];

  if (pending_report) {
    return <PendingVerificationState report={pending_report} invoice={invoice} onCancel={onExitDashboard} />;
  }

  if (invoice?.status === "payment_submitted") {
    return (
      <div className="text-center py-6" data-testid="payment-submitted-state">
        <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Icons.Clock className="h-7 w-7 text-amber-600" />
        </div>
        <h3 className="font-semibold text-slate-800 text-lg">Payment Submitted — Verification Pending</h3>
        <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
          Your payment report has been received. Our Finance team will verify the transfer and post it. Your invoice will show as Paid once verified.
        </p>
        {onExitDashboard && (
          <button onClick={onExitDashboard}
            className="mt-4 text-xs text-slate-400 hover:text-slate-600 hover:underline"
            data-testid="payment-submitted-exit">
            Exit to dashboard
          </button>
        )}
      </div>
    );
  }

  if (methods.length === 0) return (
    <div className="text-center py-8" data-testid="no-payment-methods">
      <Icons.AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
      <p className="font-semibold text-slate-700 text-sm">No payment methods available</p>
      <p className="text-xs text-slate-500 mt-1">Please contact your advisor to arrange payment.</p>
    </div>
  );

  const selectedMethod = selected === THAI_GROUP_CODE
    ? activeThaiMethod
    : (methods.find(m => m.method_code === selected) || methods[0]);

  const handleReportSubmitted = () => { load(); onReportSubmitted?.(); };
  const handlePaid = () => { load(); onPaymentInitiated?.(); };

  // THB display for invoice summary when Thai group is selected
  const showThbInSummary = selected === THAI_GROUP_CODE && activeThaiMethod?.thb_amount;

  return (
    <div data-testid="payment-method-selector">
      {/* Invoice amount summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="payment-invoice-summary">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            <span className="font-mono font-semibold text-slate-800">{invoice.invoice_number}</span>
            {invoice.balance > 0 && (
              <span className="ml-2 text-xs text-slate-400">Balance due</span>
            )}
          </div>
          <div className="text-right">
            <div className="font-bold text-[#171B2E] text-lg">{fmt(invoice.balance, invoice.currency)}</div>
          </div>
        </div>
        {/* Thai: show approximate THB amount when Thai group is selected */}
        {showThbInSummary && (
          <div className="mt-2 pt-2 border-t border-amber-200 flex items-baseline gap-2" data-testid="thb-summary-display">
            <span className="text-xs text-amber-700">≈</span>
            <span className="font-bold text-amber-800">฿{new Intl.NumberFormat("th-TH").format(Math.round(activeThaiMethod.thb_amount))}</span>
            <span className="text-xs text-amber-600">at {activeThaiMethod.usd_thb_rate?.toFixed(2) || "33.00"} THB/USD — indicative rate</span>
          </div>
        )}
      </div>

      {/* Method selector */}
      {displayMethods.length > 1 && (
        <>
          <p className="text-sm font-semibold text-slate-700 mb-3">How would you like to pay?</p>
          <div className="space-y-2 mb-4" data-testid="payment-method-options">
            {displayMethods.map(m => {
              const iconKey = m.method_code === THAI_GROUP_CODE ? "Banknote"
                            : (METHOD_ICONS[m.method_code] || "CreditCard");
              const Ic = Icons[iconKey];
              const isSel = selected === m.method_code;
              const colorCls = isSel
                ? "border-[#F5A623] bg-amber-50"
                : "border-slate-200 hover:border-slate-300";
              return (
                <label key={m.method_code}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${colorCls}`}
                  data-testid={`method-option-${m.method_code}`}>
                  <input type="radio" name="payment_method" value={m.method_code}
                    checked={isSel} onChange={() => setSelected(m.method_code)}
                    className="mt-0.5 accent-[#F5A623]" />
                  <Ic className={`h-4 w-4 shrink-0 ${isSel ? "text-[#F5A623]" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {m.method_code === THAI_GROUP_CODE ? m.display_name : (METHOD_DISPLAY[m.method_code] || m.display_name)}
                      </span>
                      {m.fee_pct > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                          +{m.fee_pct}% fee
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{m.subtitle}</span>
                    {/* Show THB estimate in method option when Thai group */}
                    {m.method_code === THAI_GROUP_CODE && m.thb_amount && (
                      <span className="text-xs text-amber-600 font-semibold mt-0.5 block" data-testid="thai-group-thb-hint">
                        ≈ ฿{new Intl.NumberFormat("th-TH").format(Math.round(m.thb_amount))} THB
                      </span>
                    )}
                    {/* Show USD settlement hint for Stripe when invoice is THB */}
                    {m.method_code === "stripe" && m.usd_amount && (
                      <span className="text-xs text-violet-600 font-semibold mt-0.5 block" data-testid="stripe-usd-hint">
                        ≈ {fmt(m.usd_amount, "USD")} USD settlement
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </>
      )}

      {/* Thai: sub-method picker (Bank Transfer vs QR Code) */}
      {selected === THAI_GROUP_CODE && thaiMethods.length > 1 && (
        <div className="mb-4" data-testid="thai-sub-method-selector">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Choose your Thai payment method</p>
          <div className="flex gap-2">
            {thaiMethods.map(m => (
              <button key={m.method_code}
                onClick={() => setThaiSubMethod(m.method_code)}
                className={`flex-1 rounded-lg border py-2 px-3 text-sm font-medium transition-colors
                  ${thaiSubMethod === m.method_code
                    ? "border-[#F5A623] bg-amber-50 text-amber-800"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid={`thai-sub-${m.method_code}`}>
                {m.method_code === "thai_bank" ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <Icons.Building2 className="h-3.5 w-3.5" />Bank Transfer
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1.5">
                    <Icons.QrCode className="h-3.5 w-3.5" />QR Code
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inline content for selected method */}
      {selectedMethod?.method_code === "stripe" && (
        <StripeContent invoice={invoice} method={selectedMethod} returnPath={returnPath} onInitiated={onPaymentInitiated} />
      )}

      {selectedMethod?.method_code === "paypal" && ppConfig?.configured && (
        <PayPalContent invoice={invoice} ppClientId={ppConfig.client_id} onPaid={handlePaid} />
      )}

      {selectedMethod?.method_code === "paypal" && !ppConfig?.configured && (
        <InstructionContent method={selectedMethod} invoice={invoice} onReportSubmitted={handleReportSubmitted} />
      )}

      {selectedMethod && !["stripe", "paypal"].includes(selectedMethod.method_code) && (
        <InstructionContent method={selectedMethod} invoice={invoice} onReportSubmitted={handleReportSubmitted} />
      )}
    </div>
  );
}
